import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import styles from './ToggleSwitch.module.css';
import InfoTooltip from '../../shared/InfoTooltip/InfoTooltip';


const ToggleSwitch = ({
  id,
  isChecked,
  onChange,
  label,
  style,
  inputStyle,
  variant = 'default', // 'default' or 'split'
  onLabel,
  offLabel,
  disabled = false,
  tooltip = '',
}) => {
  const uniqueLabelId = useMemo(() => `${id}-label-${uuidv4().slice(0, 8)}`, [id]);
  const accessibleLabel = label || `Toggle switch ${isChecked ? 'on' : 'off'}`;
  const displayOnLabel = onLabel || 'ON';
  const displayOffLabel = offLabel || 'OFF';

  const renderTooltip = () => <InfoTooltip text={tooltip} />;

  return (
    <div className={`${styles.switchContainer} ${disabled ? styles.disabled : ''}`} style={style}>      {variant === 'default' ? (
        <>
          <label className={styles.switch} htmlFor={id}>
            <input
              type="checkbox"
              id={id}
              checked={isChecked}
              onChange={onChange}
              style={inputStyle}
              aria-labelledby={uniqueLabelId}
              aria-checked={isChecked}
              disabled={disabled}
            />
            <span className={styles.slider}></span>
          </label>
          <label id={uniqueLabelId} htmlFor={id} className={styles.switchLabel}>
            <span className={styles.statusText}>{accessibleLabel}</span>
          </label>
          {renderTooltip()}
        </>
      ) : (
        <>
          <label className={styles.splitToggleLabel} htmlFor={id}>
            <input
              type="checkbox"
              id={id}
              checked={isChecked}
              onChange={onChange}
              className={styles.splitToggleInput}
              aria-labelledby={uniqueLabelId}
              aria-checked={isChecked}
              disabled={disabled}
            />
            <div className={styles.splitToggle}>
              <div className={`${styles.splitSide} ${styles.onSide} ${isChecked ? styles.active : ''}`}>
                {displayOnLabel}
              </div>
              <div className={`${styles.splitSide} ${styles.offSide} ${!isChecked ? styles.active : ''}`}>
                {displayOffLabel}
              </div>
            </div>
          </label>
          <span id={uniqueLabelId} className={styles.visuallyHidden}>{accessibleLabel}</span>
          {renderTooltip()}
        </>
      )}
    </div>
  );
};

ToggleSwitch.propTypes = {
  id: PropTypes.string.isRequired,
  isChecked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string, // Recommended for accessibility
  style: PropTypes.object,
  inputStyle: PropTypes.object,
  variant: PropTypes.oneOf(['default', 'split']),
  onLabel: PropTypes.string,
  offLabel: PropTypes.string,
  disabled: PropTypes.bool,
  tooltip: PropTypes.string
};

ToggleSwitch.defaultProps = {
  variant: 'default',
  label: '',
  style: {},
  inputStyle: {},
  onLabel: 'ON',
  offLabel: 'OFF',
  disabled: false,
  tooltip: ''
};

export default ToggleSwitch;